"use client";

import { ArrowRight, Images, Printer, QrCode } from "lucide-react";
import { useTranslations } from "next-intl";

import Reveal from "@/components/index/Reveal";
import { SECTION_GUTTER } from "@/components/index/sectionGutters";
import { cn } from "@/lib/utils";
import { Link } from "@/navigation";

// Tile colours follow the event cards (EventTypeMarquee). Each step slides in
// from its own distance and tilt so the row doesn't land in lockstep.
const STEPS = [
  {
    key: "Point1",
    Icon: QrCode,
    tileClassName: "bg-[#FFF6E7] text-[#9A5A00]",
    hoverClassName: "can-hover:group-hover:-rotate-[8deg]",
    reveal: { y: 34, rotate: -4, delay: 0, duration: 680 },
  },
  {
    key: "Point2",
    Icon: Printer,
    tileClassName: "bg-[#FBF2EE] text-[#7E5645]",
    hoverClassName: "can-hover:group-hover:rotate-[7deg]",
    reveal: { y: 48, rotate: 3, delay: 120, duration: 760 },
  },
  {
    key: "Point3",
    Icon: Images,
    tileClassName: "bg-[#F3EFFF] text-[#57428A]",
    hoverClassName: "can-hover:group-hover:-rotate-[5deg]",
    reveal: { y: 40, rotate: -2, delay: 230, duration: 720 },
  },
] as const;

interface StepsCardProps {
  showTitle?: boolean;
  /** The homepage links to the How it works page from its guest video instead. */
  showPageLink?: boolean;
}

export default function StepsCard({
  showTitle = false,
  showPageLink = true,
}: StepsCardProps): React.JSX.Element {
  const t = useTranslations("Index.StepsCard");
  const tNav = useTranslations("MainNav");
  // Step titles are headings only under the section title.
  const StepTitle = showTitle ? "h3" : "p";

  return (
    <section
      className={cn("max-w-screen-lg scroll-mt-28 mx-2", SECTION_GUTTER, "lg:mx-auto")}
      id="how-it-works"
    >
      {showTitle ? (
        <h2 className="mb-10 text-center text-3xl font-bold tracking-tight md:text-4xl">
          {t("title")}
        </h2>
      ) : null}

      <div className="relative rounded-[2rem] border bg-white p-6 shadow-sm md:p-10">
        {/* Runs from the first tile's centre to the last one's. */}
        <Reveal
          aria-hidden="true"
          className="pointer-events-none absolute left-[4.25rem] right-[calc(100%/3_-_2.25rem)] top-[3.75rem] hidden md:block"
          y={0}
        >
          <svg
            className="reveal-trail h-4 w-full text-primary"
            fill="none"
            preserveAspectRatio="none"
            viewBox="0 0 600 16"
          >
            <path
              d="M0 8 C 75 -2, 150 18, 300 8 S 525 -2, 600 8"
              stroke="currentColor"
              strokeDasharray="6 9"
              strokeLinecap="round"
              strokeWidth="2"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        </Reveal>

        {/* Safari drops list semantics once list-style is removed. */}
        <ol className="relative grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-8" role="list">
          {STEPS.map(({ key, Icon, tileClassName, hoverClassName, reveal }, index) => (
            <Reveal as="li" key={key} className="group flex gap-4 md:flex-col" {...reveal}>
              <span
                className={cn(
                  "relative grid h-14 w-14 shrink-0 place-items-center rounded-2xl transition-transform duration-500 ease-[var(--ease-overshoot)] can-hover:group-hover:scale-110",
                  tileClassName,
                  hoverClassName,
                )}
              >
                <Icon aria-hidden="true" className="h-7 w-7" />
                <span
                  aria-hidden="true"
                  className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-dark text-xs font-bold text-white ring-2 ring-white"
                >
                  {index + 1}
                </span>
              </span>
              <div className="flex flex-col gap-1.5">
                <StepTitle className="font-bold">{t(`${key}.title`)}</StepTitle>
                <p className="text-muted-foreground">{t(`${key}.body`)}</p>
              </div>
            </Reveal>
          ))}
        </ol>
      </div>
      {showPageLink ? (
        <Link
          href="/how-it-works"
          className="group mt-4 inline-flex items-center gap-2 px-2 text-sm font-semibold text-[#8a5a1c] hover:underline"
        >
          {tNav("howItWorks")}
          <ArrowRight
            aria-hidden="true"
            className="h-4 w-4 transition-transform duration-300 ease-[var(--ease-overshoot)] can-hover:group-hover:translate-x-0.5"
          />
        </Link>
      ) : null}
    </section>
  );
}
